import Header, { HeaderProps } from "components/layout/Header/Header";
import Footer, { FooterProps } from "components/layout/Footer/Footer";
import styles from "./Layout.module.scss";
import classNames from "classnames";
import { ReactComponentPropsBase } from "../../../base-props/ReactComponentPropsBase";
import { AlertBannerProps } from "components/layout/AlertBanner/AlertBanner";
import Breadcrumbs, {
  BreadcrumbsProps,
} from "components/layout/Breadcrumbs/Breadcrumbs";
import { BypassBannerProps } from "components/layout/BypassBanner/BypassBanner";
import { SiteContext } from "../../../utils/useSite";
import { ThemeContext } from "../../../utils/useTheme";
import { IReactComponentProps } from "src/base-props/IReactComponentProps";
import * as Components from "../../../MIKA.Components";
import "./Layout.css";

export interface MIKAComponent extends ReactComponentPropsBase {
  componentName: keyof typeof Components;
  component: IReactComponentProps;
}

export interface LayoutProps extends ReactComponentPropsBase {
  alertBanner?: AlertBannerProps;
  bypassBanner?: BypassBannerProps;
  header?: HeaderProps;
  footer?: FooterProps;
  component: MIKAComponent;
  epiFullRefreshPropertyNames?: string[];
  breadcrumbs?: BreadcrumbsProps;
  theme?: "white" | "green" | "dark";
}

export interface ReactPropsBase {}

const MIKATestLayoutContent = ({
  Comp,
  epiFullRefreshPropertyNames,
  component,
  footer,
}: {
  Comp: React.FC<any>;
  epiFullRefreshPropertyNames?: string[];
  component: MIKAComponent;
  footer?: FooterProps;
}) => {
  return (
    <SiteContext.Provider value="mika">
      <div className={styles.layout}>
        <div className="block-display">
          {/* @ts-ignore */}
          {Comp && <Comp {...component.component} />}
        </div>
        {footer && <Footer {...footer} />}
        {epiFullRefreshPropertyNames && (
          <input
            type="hidden"
            data-epi-full-refresh-property-names={epiFullRefreshPropertyNames?.join(
              ","
            )}
          />
        )}
      </div>
    </SiteContext.Provider>
  );
};

export const Layout = ({
  alertBanner,
  bypassBanner,
  header,
  footer,
  component,
  epiFullRefreshPropertyNames,
  breadcrumbs,
  theme,
}: LayoutProps) => {
  const Comp = Components[component.componentName] as React.FC<any>;
  const isMangfoldsTest = ["MangfoldstestenPage"].includes(
    component.componentName
  );
  const isMangfoldsTestResult = ["MangfoldstestResultPage"].includes(
    component.componentName
  );

  if (isMangfoldsTest)
    return (
      <MIKATestLayoutContent
        {...{ Comp, epiFullRefreshPropertyNames, component }}
      />
    );
  if (isMangfoldsTestResult)
    return (
      <MIKATestLayoutContent
        {...{ Comp, epiFullRefreshPropertyNames, component, footer }}
      />
    );

  return (
    <SiteContext.Provider value="mika">
      <ThemeContext.Provider value={theme ? theme : "white"}>
        <div
          className={classNames(styles.layout, theme ? styles[theme] : null)}
        >
          <div className="block-display">
            <div className={classNames(styles.headerContainer)}>
              {header && (
                <Header
                  {...header}
                  alertBanner={alertBanner}
                  bypassBanner={bypassBanner}
                />
              )}
              {breadcrumbs && <Breadcrumbs {...breadcrumbs} />}
            </div>

            {Comp && (
              <div id="page-content">
                {/* @ts-ignore */}
                <Comp {...component.component} />
              </div>
            )}
          </div>
          {footer && <Footer {...footer} />}
          {epiFullRefreshPropertyNames && (
            <input
              type="hidden"
              data-epi-full-refresh-property-names={epiFullRefreshPropertyNames?.join(
                ","
              )}
            />
          )}
        </div>
      </ThemeContext.Provider>
    </SiteContext.Provider>
  );
};

export default Layout;
